import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router'
import { SettingsIcon, SplitIcon } from 'lucide-react'
import { useState } from 'react'
import { EmptyState } from '@/components/empty-state'
import { Favicon } from '@/components/favicon'
import { Button } from '@/components/ui/button'
import { useCompetitors } from '@/lib/competitors'
import { formatCount } from '@/lib/format'
import { cn } from '@/lib/utils'
import { TableSkeleton, useSiteScope } from '../-section'
import { AddCompetitorDialog } from './-parts'

/**
 * The section is one comparison read several ways, so it gets the sidebar the
 * link profile has: the gap across every competitor, then each of them on their
 * own, then the list itself.
 *
 * Managing the set is an entry rather than a control on the report, because
 * adding a competitor is the one thing here that is a write — and a button that
 * spends money parked above a table reads as a filter on it. It sits last for
 * the same reason settings does in the tab row.
 *
 * The layout is pathless so nothing deeper is narrowed by a nav it cannot use,
 * and each competitor is a real page: linkable, openable in a tab, with its own
 * cut and its own paging.
 */
export const Route = createFileRoute('/_app/$org/$host/competitors/_list')({
  component: CompetitorsLayout,
})

function CompetitorsLayout() {
  const { org, host, site } = useSiteScope()
  const { data, isPending } = useCompetitors(site)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const leaf = pathname.replace(/\/$/, '').split('/').at(-1)
  const [adding, setAdding] = useState(false)

  if (isPending || !data) {
    return (
      <>
        <h1 className="sr-only">Competitors</h1>
        <TableSkeleton />
      </>
    )
  }

  // No competitors is a state of the whole section rather than of one entry, so
  // it answers here instead of behind a nav offering three ways to read the
  // same nothing.
  if (data.competitors.length === 0) {
    return (
      <>
        <h1 className="sr-only">Competitors</h1>
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            title="No competitors yet"
            description={`Name a domain ${host} competes with and the links it has that ${host} does not become a list.`}
            action={
              <Button size="sm" onClick={() => setAdding(true)}>
                Add a competitor
              </Button>
            }
          />
        </div>
        <AddCompetitorDialog
          open={adding}
          onClose={() => setAdding(false)}
          siteId={site?.id}
          host={host}
        />
      </>
    )
  }

  return (
    <div className="flex flex-col gap-8 sm:flex-row">
      <nav className="flex flex-wrap gap-1 sm:w-52 sm:shrink-0 sm:flex-col sm:flex-nowrap">
        <Link
          to="/$org/$host/competitors"
          params={{ org, host }}
          className={navClass(leaf === 'competitors')}
        >
          <NavBody
            icon={
              <SplitIcon
                className={cn('size-3.5', leaf === 'competitors' ? 'text-plum' : 'opacity-70')}
              />
            }
            label="Link gap"
            count={data.gapTotal}
          />
        </Link>

        {/* Each competitor is its own reading of the same feed, and the favicon
            is what makes the row scannable — the domain is the label, and its
            own mark is what the eye lands on before reading it. */}
        <span className="mono-label mt-4 mb-1 px-2.5 text-muted-foreground/70">Their links</span>

        {data.competitors.map((competitor) => (
          <Link
            key={competitor.id}
            to="/$org/$host/competitors/$competitorId"
            params={{ org, host, competitorId: competitor.id }}
            className={navClass(leaf === competitor.id)}
          >
            <NavBody
              icon={<Favicon host={competitor.host} size={14} className="shrink-0 rounded-[3px]" />}
              label={competitor.host}
              count={competitor.storedDomains}
            />
          </Link>
        ))}

        <Link
          to="/$org/$host/competitors/manage"
          params={{ org, host }}
          className={cn(navClass(leaf === 'manage'), 'mt-4')}
        >
          <NavBody
            icon={
              <SettingsIcon
                className={cn('size-3.5', leaf === 'manage' ? 'text-plum' : 'opacity-70')}
              />
            }
            label="Manage"
          />
        </Link>
      </nav>

      <div className="min-w-0 flex-1 space-y-4">
        <Outlet />
      </div>
    </div>
  )
}

function navClass(active: boolean): string {
  return cn(
    'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
    active
      ? 'bg-plum-selected text-foreground'
      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
  )
}

function NavBody({
  icon,
  label,
  count,
}: {
  icon: React.ReactNode
  label: string
  /** Omitted where there is nothing to count. A zero beside an entry that does
   * not hold rows is a fact stated about the wrong thing. */
  count?: number
}) {
  return (
    <>
      {icon}
      <span className="min-w-0 truncate font-mono text-[12.5px]">{label}</span>
      {count !== undefined && (
        <span className="ml-auto pl-2 font-mono text-[11px] text-muted-foreground tabular">
          {formatCount(count)}
        </span>
      )}
    </>
  )
}
